import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { pharmacyApi } from "@/api/pharmacy";
import { RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PaymentMethod } from "@/types/types";

export default function PharmacyReturnsPage() {
  const [billSuffix, setBillSuffix] = useState("");
  const [reason, setReason] = useState("");
  const [bill, setBill] = useState<any>(null);
  const [returnQtyByIssueId, setReturnQtyByIssueId] = useState<Record<string, string>>({});
  const [refundAmount, setRefundAmount] = useState<string>("");
  const [refundMethod, setRefundMethod] = useState<PaymentMethod>(PaymentMethod.CASH);

  const lookupMutation = useMutation({
    mutationFn: async () => {
      const suffix = billSuffix.trim();
      if (!suffix) throw new Error("Enter last digits of bill no");
      const res = await pharmacyApi.getReturnBillBySuffix(suffix);
      return res.data?.data;
    },
    onSuccess: (data) => {
      setBill(data);
      const next: Record<string, string> = {};
      (data?.items ?? []).forEach((it: any) => {
        if (it?.pharmacyIssueId) next[it.pharmacyIssueId] = "";
      });
      setReturnQtyByIssueId(next);
    },
    onError: (err: any) => {
      setBill(null);
      toast.error(err?.response?.data?.message ?? err?.message ?? "Lookup failed");
    }
  });

  const returnByBillMutation = useMutation({
    mutationFn: async () => {
      if (!bill?.id) throw new Error("Lookup a bill first");
      const lines = Object.entries(returnQtyByIssueId)
        .map(([pharmacyIssueId, qtyStr]) => ({
          pharmacyIssueId,
          quantityReturned: parseInt(qtyStr, 10) || 0
        }))
        .filter((x) => x.quantityReturned > 0);
      if (lines.length === 0) throw new Error("Enter return quantity for at least one item");
      const refund = refundAmount.trim() ? Number(refundAmount) : 0;
      if (refund < 0) throw new Error("Refund amount cannot be negative");
      return pharmacyApi.returnByBill({
        billId: bill.id,
        returns: lines,
        reason: reason.trim() || undefined,
        refundAmount: refund,
        refundMethod: refund > 0 ? refundMethod : undefined
      });
    },
    onSuccess: () => {
      toast.success("Return processed; stock and bill adjusted.");
      setBillSuffix("");
      setReason("");
      setBill(null);
      setReturnQtyByIssueId({});
      setRefundAmount("");
      setRefundMethod(PaymentMethod.CASH);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message ?? err?.message ?? "Return failed");
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    returnByBillMutation.mutate();
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5" />
            Pharmacy Return
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Enter the last digits of the bill number to load items. Returning items will add stock back to the same batch and adjust the bill totals.
          </p>
          <div className="space-y-4 max-w-2xl">
            <div>
              <Label htmlFor="billSuffix">Bill no (last 6 digits)</Label>
              <Input
                id="billSuffix"
                value={billSuffix}
                onChange={(e) => setBillSuffix(e.target.value)}
                placeholder="e.g. last 6 of BILL-...-ABC123"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => lookupMutation.mutate()}
              disabled={lookupMutation.isPending}
            >
              {lookupMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Load bill"}
            </Button>

            {bill && (
              <div className="rounded-lg border p-4 space-y-3 bg-muted/20">
                <div className="text-sm">
                  <div><span className="font-medium">Bill:</span> {bill.billNumber}</div>
                  <div><span className="font-medium">Patient:</span> {bill.patient?.name} ({bill.patient?.patientUniqueId})</div>
                </div>

                <div className="space-y-2">
                  {(bill.items ?? []).length === 0 ? (
                    <div className="text-sm text-muted-foreground">No medicine items found in this bill.</div>
                  ) : (
                    (bill.items ?? []).map((it: any) => (
                      <div key={it.pharmacyIssueId ?? it.billItemId} className="flex flex-wrap items-center justify-between gap-2 border rounded-md p-3 bg-white">
                        <div className="min-w-[220px]">
                          <div className="text-sm font-medium">{it.drug?.name ?? it.description}</div>
                          <div className="text-xs text-muted-foreground">
                            Sold: {it.quantity} | Returned: {it.returnedQty} | Returnable: {it.returnableQty}
                          </div>
                          {it.batch?.batchNo && (
                            <div className="text-xs text-muted-foreground">Batch: {it.batch.batchNo}</div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Label className="text-xs">Return qty</Label>
                          <Input
                            type="number"
                            min={0}
                            max={it.returnableQty}
                            className="w-24 h-8"
                            value={returnQtyByIssueId[it.pharmacyIssueId] ?? ""}
                            onChange={(e) =>
                              setReturnQtyByIssueId((p) => ({ ...p, [it.pharmacyIssueId]: e.target.value }))
                            }
                            disabled={!it.pharmacyIssueId || it.returnableQty <= 0 || returnByBillMutation.isPending}
                            placeholder="0"
                          />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="reason">Reason (optional)</Label>
              <Input
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Patient allergy"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="refundAmount">Refund amount (₹)</Label>
                <Input
                  id="refundAmount"
                  type="number"
                  min={0}
                  step="0.01"
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  placeholder="0"
                />
              </div>
              <div>
                <Label htmlFor="refundMethod">Refund mode</Label>
                <select
                  id="refundMethod"
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                  value={refundMethod}
                  onChange={(e) => setRefundMethod(e.target.value as PaymentMethod)}
                  disabled={!refundAmount || Number(refundAmount) <= 0}
                >
                  {Object.values(PaymentMethod).map((m) => (
                    <option key={m} value={m}>{m.replace(/_/g, " ")}</option>
                  ))}
                </select>
              </div>
            </div>
            <form onSubmit={handleSubmit}>
              <Button type="submit" disabled={returnByBillMutation.isPending || !bill}>
                {returnByBillMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit return"}
              </Button>
            </form>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
